import React, { FC, useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { ReactSVG } from 'react-svg'
import { RootState } from '../../../../states/containers/store'
import { URL } from '../../../constants/url'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SearchResult from '../../../components/search/SearchResult'
import { format, nowDate, dateManipulateDay, yesterDay, toDate, diffDays, correctionDate } from '../../../../utils/date'
import {
  fetchSearchRequest,
  fetchHistory,
  fetchKeyword,
  initialSearchResult,
  createSearchCondition,
  updateSearchCondition,
  fetchSearchCondition,
  delSearchCondition,
} from '../../../../states/containers/search/listSlice'
import { useTranslation } from 'react-i18next'
import { DateRange } from 'react-date-range'
import { ja, enUS } from 'date-fns/locale'
import ChipInput from 'material-ui-chip-input'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { Chip } from '@material-ui/core'
import { getGetPrams, setPath } from '../../../../utils/url'
import { fetchCompanyService } from '../../../../states/containers/setting/systemSlice'
import SearchConditionsModal from '../../../components/search/SearchConditionsModal'
import MiniMenu from '../../../components/search/MiniMenu'
import ConfirmationModal from '../../../components/search/ConfirmationModal'
import CategoryAndAppKeywordInput from '../../../components/search/CategoryAndAppKeywordInput'
import TeamAndMemberKeywordInput from '../../../components/search/TeamAndMemberKeywordInput'
import { useAlert } from 'react-alert'
import { hiraToKana, kanaToHira } from '../../../../utils/string'
import ReactWordcloud from 'react-wordcloud'

import '../../../assets/scss/search.scss'
import ScoreKeywordInput from '../../../components/search/ScoreKeywordInput'
import CATEGORY from '../../../constants/category'

type LangValue = 'ja' | 'en'

interface KeywordParam {
  rank: number
  word: string
  value: number
}

interface SearchRequestParams {
  start_date: string
  end_date: string
  team_ids: number[]
  member_ids: number[]
  keywords: string[]
  service_names: string[]
  category_ids: number[]
  efficiency_level: string[]
  stress_level: string[]
  focus_level: string[]
  lower_limit_time?: number
  sort_key?: string
  sort_type?: string
}

interface SearchResponseParams {
  summary: {
    biz: {
      total_value: number
      name_labels: string[]
      value_labels: number[]
    },
    elapsed_time: {
      total_value: number
      name_labels: string[]
      value_labels: number[]
    },
    count: {
      total_value: number
      name_labels: string[]
      value_labels: number[]
    }
  } | undefined,
  result_list: SearchResult[] | undefined
  tags: string[]
  search_result: boolean
  search_request: SearchRequestParams | undefined
}

interface Word {
  word: string
  check: boolean
  display: boolean
}

interface Keyword {
  rank?: number
  word: string
  value: number
  check: boolean
  display: boolean
}

interface Error {
  name: string,
  message: string,
}

interface GetParams {
  start_date: string
  end_date: string
  search_word: string
  team_ids: string
  member_ids: string
  keywords: string
  service_names: string
  category_ids: string
  efficiency: string
  stress: string
  focus: string
  sort_key?: string
  sort_type?: string
  wordcloud: string
  referrer?: string
}

interface SearchConditionsModalState {
  display: boolean,
  type: string,
  title?: string,
  id?: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (title: string, id?: number) => void | VoidFunction,
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  title: string,
  id: number,
  callback: VoidFunction,
}

interface SearchValues {
  start_date: string
  end_date: string
  team_names: string[]
  member_names: string[]
  keywords: string[]
  service_names: string[]
  category_names: string[]
  efficiency_level: string[]
  stress_level: string[]
  focus_level: string[]
  lower_limit_time?: number
  sort_key?: string
  sort_type?: string
}

const Index: FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const alert = useAlert()

  const HISTORY_DISPLAY_LIMIT = 10

  const SERVICE_NAME_DISPLAY_LIMIT = 1000

  const KEYWORD_DISPLAY_LIMIT = 20

  const CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT = 10

  const KEYWORD_SELECT_LIMIT = 20

  const WORD_CLOUD_KEYWORD_DISPLAY_LIMIT = 100

  const LOWER_LIMIT_TIME_DEFAULT = 300

  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector((state: RootState) => state)
  const {
    teams,
    company,
    members,
    team_manager_ids,
  } = useSelector((state: RootState) => (state.commonPart))
  const {
    isLoading,
    searchHistory,
    searchKeyword,
    searchResult,
    searchCondition,
  } = useSelector((state: RootState) => state.search)
  const {
    company_service,
  } = useSelector((state: RootState) => state.system)

  const efficiencyScoreList = [
    {
      name: t('search.conditions.score.efficiency_a'),
      value: 'a',
    },
    {
      name: t('search.conditions.score.efficiency_c'),
      value: 'c',
    },
  ]

  const stressScoreList = [
    {
      name: t('search.conditions.score.stress_a'),
      value: 'a',
    },
    {
      name: t('search.conditions.score.stress_c'),
      value: 'c',
    },
  ]

  const focusScoreList = [
    {
      name: t('search.conditions.score.focus_a'),
      value: 'a',
    },
    {
      name: t('search.conditions.score.focus_c'),
      value: 'c',
    },
  ]

  const searchValueInit:SearchValues = {
    start_date: `${format(correctionDate(dateManipulateDay(nowDate(), -7)), 'YYYY-MM-DD')}`,
    end_date: `${format(correctionDate(yesterDay('YYYY-MM-DD')), 'YYYY-MM-DD')}`,
    team_names: [],
    member_names: [],
    keywords: [],
    service_names: [],
    category_names: [],
    efficiency_level: [],
    stress_level: [],
    focus_level: [],
    lower_limit_time: LOWER_LIMIT_TIME_DEFAULT,
  }

  const searchConditionsModalStateInit: SearchConditionsModalState = {
    display: false,
    type: "",
    callback: () => {
      // none
    },
  }

  const [searchConditionsModalState, setSearchConditionsModalState] = useState<SearchConditionsModalState>(searchConditionsModalStateInit)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    title: "",
    id: 0,
    callback: () => {
      // none
    },
  })

  const [miniMenuDisplay, setMiniMenuDisplay] = useState<boolean>(false)

  const [searchValue, setSearchValue] = useState(searchValueInit)

  const [searchResultList, setSearchResultList] = useState<SearchResponseParams>(
    { summary: undefined, result_list: undefined, tags: [], search_result: true, search_request: undefined },
  )
  const [searchResultLoading, setSearchResultLoading] = useState(false)
  const [isSearch, setIsSearch] = useState(false)

  const resultTopRef = useRef() as React.MutableRefObject<HTMLInputElement>
  const scrollToResultTop = () => resultTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })

  const [dateErrorMessage, setDateErrorMessage] = useState('')
  const [keywordErrorMessage, setKeywordErrorMessage] = useState('')

  const [searchErrorMessage, setSearchErrorMessage] = useState('')

  const [clickWord, setClickWord] = useState('')
  const [isWordCloudLoading, setIsWordCloudLoading] = useState<boolean>(true)

  const [isSubmit, setIsSubmit] = useState<boolean>(false)

  const [isDisplayWordCloud, setIsDisplayWordCloud] = useState<boolean>(
    (getGetPrams(location.search) as GetParams).wordcloud
      ? (getGetPrams(location.search) as GetParams).wordcloud === "true"
      : false,
  )

  const [isSwitchButton, setIsSwitchButton] = useState(true)

  const validateFormValues = (values: SearchValues) => {
    const errorArray: Error[] = []
    const diff = diffDays(values.start_date, values.end_date)
    if (diff < 0) {
      errorArray.push({ name: 'date', message: t('search.index.form.error.future_day') })
    } else if (diff >= 180) {
      errorArray.push({ name: 'date', message: t('search.index.form.error.over_180_days') })
    }
    if (values.service_names.length > CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT) {
      errorArray.push({
        name: 'service_name',
        message: t('search.index.form.error.limit_category_and_service_name', { num: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT }),
      })
    }
    if (values.keywords.length > KEYWORD_SELECT_LIMIT) {
      errorArray.push({ name: 'keyword', message: t('search.index.form.error.limit_keyword', { num: KEYWORD_SELECT_LIMIT }) })
    }
    if (
      values.service_names.length === 0
      && values.keywords.length === 0
      && values.category_names.length === 0
      && values.team_names.length === 0
      && values.member_names.length === 0
      && values.efficiency_level.length === 0
      && values.stress_level.length === 0
      && values.focus_level.length === 0
    ) {
      errorArray.push({ name: 'search', message: t('search.index.form.error.no_select') })
    }
    return errorArray
  }

  // date
  const [isDateRangeDisplay, setIsDateRangeDisplay] = useState(false)

  const openDateRangeModal = () => setIsDateRangeDisplay(true)
  const closeDateRangeModal = () => setIsDateRangeDisplay(false)

  const [isChangeDate, setIsChangeDate] = useState<boolean>(true)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changeDateRange = (ranges: any) => {
    if (
      searchValue.start_date === format(ranges.selection.startDate, "YYYY-MM-DD")
      && searchValue.end_date === format(ranges.selection.endDate, "YYYY-MM-DD")
    ) return
    setSearchValue({
      ...searchValue,
      start_date: format(ranges.selection.startDate, "YYYY-MM-DD"),
      end_date: format(ranges.selection.endDate, "YYYY-MM-DD"),
    })
    const diff = diffDays(format(ranges.selection.startDate, "YYYY-MM-DD"), format(ranges.selection.endDate, "YYYY-MM-DD"))
    if (diff < 0) {
      setDateErrorMessage(t('search.index.form.error.future_day'))
    } else if (diff >= 180) {
      setDateErrorMessage(t('search.index.form.error.over_180_days'))
    } else {
      setDateErrorMessage("")
      setIsChangeDate(true)
    }
  }

  // team_id
  const setTeamNames = (values: string[]) => {
    setSearchValue({
      ...searchValue,
      team_names: values,
    })
  }

  // member_id
  const setMemberNames = (values: string[]) => {
    setSearchValue({
      ...searchValue,
      member_names: values,
    })
  }

  const resetTeamNamesAndMemberNames = () => {
    setSearchValue({
      ...searchValue,
      team_names: [],
      member_names: [],
    })
  }

  // app name
  const setServiceNames = (values: string[]) => {
    setSearchValue({
      ...searchValue,
      service_names: values,
    })
  }

  // category name
  const setCategoryNames = (values: string[]) => {
    setSearchValue({
      ...searchValue,
      category_names: values,
    })
  }

  // switch button lower_limit_time
  const switchLowerLimitTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSwitchButton(e.target.checked)
    setSearchValue({
      ...searchValue,
      lower_limit_time: e.target.checked ? LOWER_LIMIT_TIME_DEFAULT : 0,
    })
  }

  const resetServiceNamesAndCategoryNames = () => {
    setSearchValue({
      ...searchValue,
      service_names: [],
      category_names: [],
    })
  }

  // efficiency
  const setEfficiencyList = (values: string[]) => {
    setSearchValue({
      ...searchValue,
      efficiency_level: values,
    })
  }

  // stress
  const setStressList = (values: string[]) => {
    setSearchValue({
      ...searchValue,
      stress_level: values,
    })
  }

  // focus
  const setFocusList = (values: string[]) => {
    setSearchValue({
      ...searchValue,
      focus_level: values,
    })
  }

  const resetScore = () => {
    setSearchValue({
      ...searchValue,
      efficiency_level: [],
      stress_level: [],
      focus_level: [],
    })
  }

  const [isKeywordDisplay, setIsKeywordDisplay] = useState(false)

  const [historyList, setHistoryList] = useState<Word[]>([])

  const initHistoryList = (words: string[]) => {
    const newHistoryList = words.map((value) => ({ word: value, check: false, display: true }))
    setHistoryList(newHistoryList)
  }

  const addWordToHistory = (searchWords: string[]) => {
    const newHistoryList = historyList.filter((value) => !searchWords.includes(value.word))
    const addHistoryList = searchWords.map((value) => ({ word: value, check: true, display: true }))
    newHistoryList.unshift(...addHistoryList.reverse())
    setHistoryList(newHistoryList.slice(0, HISTORY_DISPLAY_LIMIT))
  }

  const keywordListInit: Keyword[] = []
  const [keywordList, setKeywordList] = useState(keywordListInit)
  const [wordCloudKeywordList, setWordCloudKeywordList] = useState(keywordListInit)

  const buildKeywordList = (buildKeywords: KeywordParam[]) => {
    const newKeywordList = buildKeywords.map((value, index: number) => (
      {
        rank: value.rank,
        word: value.word,
        value: value.value,
        check: searchValue.keywords.includes(value.word),
        display: (index < KEYWORD_DISPLAY_LIMIT),
      }))
    setKeywordList(newKeywordList)
  }

  const buildWordCloudKeywordList = (buildKeywords: KeywordParam[]) => {
    const newKeywordList = buildKeywords.map((value, index: number) => (
      {
        rank: value.rank,
        word: value.word,
        value: value.value,
        check: searchValue.keywords.includes(value.word),
        display: (index < WORD_CLOUD_KEYWORD_DISPLAY_LIMIT),
      }))
    setWordCloudKeywordList(newKeywordList)
  }

  const resetHistoryList = () => {
    const updateList = historyList.map((value, index: number) => (
      { ...value, check: searchValue.keywords.includes(value.word), display: (index < HISTORY_DISPLAY_LIMIT) }))
    setHistoryList(updateList)
  }

  const resetKeywordList = () => {
    const updateList = keywordList.map((value, index: number) => (
      { ...value, check: searchValue.keywords.includes(value.word), display: (index < KEYWORD_DISPLAY_LIMIT) }))
    setKeywordList(updateList)
  }

  const resetKeywordSuggest = () => {
    resetHistoryList()
    resetKeywordList()
  }

  const synchronizeHistoryChip = (keyword: string, deleteMode = false) => {
    const updateList = historyList.map((value) => {
      if (value.word === keyword) {
        return { ...value, check: !deleteMode }
      }
      return value
    })
    setHistoryList(updateList)
  }

  const synchronizeKeywordChip = (keyword: string, deleteMode = false) => {
    const updateList = keywordList.map((value) => {
      if (value.word === keyword) {
        return { ...value, check: !deleteMode }
      }
      return value
    })
    setKeywordList(updateList)
  }

  const resetHistoryChip = () => {
    const updateList = historyList.map((value) => ({ ...value, check: false }))
    setHistoryList(updateList)
  }

  const resetKeywordChip = () => {
    const updateList = keywordList.map((value) => ({ ...value, check: false }))
    setKeywordList(updateList)
  }

  const [tmpKeyword, setTmpKeyword] = useState<string>('')

  const [keywordHintMessage, setKeywordHintMessage] = useState('')

  const checkKeywordLimit = () => {
    if ((searchValue.keywords.length + 1) === KEYWORD_SELECT_LIMIT) {
      setIsKeywordDisplay(false)
      setKeywordHintMessage(t('search.index.form.limit_keyword', { num: KEYWORD_SELECT_LIMIT }))
    } else if ((searchValue.keywords.length + 1) >= KEYWORD_SELECT_LIMIT) {
      setIsKeywordDisplay(false)
      setKeywordHintMessage(t('search.index.form.limit_keyword', { num: KEYWORD_SELECT_LIMIT }))
      return true
    }
    return false
  }

  const addKeyword = (keyword: string) => {
    setKeywordErrorMessage('')
    if (checkKeywordLimit()) {
      return
    }
    setSearchValue({
      ...searchValue,
      keywords: [...searchValue.keywords, keyword],
    })
    synchronizeHistoryChip(keyword)
    synchronizeKeywordChip(keyword)
    setTmpKeyword('')
  }

  const deleteKeyword = (keyword: string, index: number) => {
    const newKeywords = [...searchValue.keywords]
    newKeywords.splice(index, 1)
    setSearchValue({
      ...searchValue,
      keywords: newKeywords,
    })
    synchronizeHistoryChip(keyword, true)
    synchronizeKeywordChip(keyword, true)
    setKeywordHintMessage('')
    setClickWord('')
  }

  const blurKeyword = (e: React.FocusEvent<HTMLDivElement>) => {
    const inputValue = (e.currentTarget as HTMLInputElement).value.trim()
    if (inputValue === "") {
      return
    }
    setTmpKeyword(inputValue)
  }

  const clickWordCloud = (keyword: string) => {
    setClickWord(keyword)
  }

  const openKeywordModal = () => {
    setIsKeywordDisplay(true)
    resetKeywordSuggest()
  }

  const closeKeywordModal = () => {
    if (tmpKeyword !== "") {
      addKeyword(tmpKeyword)
    }
    setIsKeywordDisplay(false)
    resetKeywordSuggest()
  }

  const inputKeyword = (keyword: string) => {
    addKeyword(keyword)
    closeKeywordModal()
  }

  const wordHistorySelect = (word: string) => {
    let count = 0
    const lowerCasedWord = word.toLowerCase()
    const hiragana = kanaToHira(word)
    const katakana = hiraToKana(word)
    const updateList = historyList.map((item) => {
      if ((item.word.toLowerCase().indexOf(lowerCasedWord) > -1 || item.word.indexOf(hiragana) > -1 || item.word.indexOf(katakana) > -1)
        && count < KEYWORD_DISPLAY_LIMIT) {
        count += 1
        return { ...item, display: true }
      }
      return { ...item, display: false }
    })
    setHistoryList(updateList)
  }

  const wordKeywordSelect = (word: string) => {
    let count = 0
    const lowerCasedWord = word.toLowerCase()
    const hiragana = kanaToHira(word)
    const katakana = hiraToKana(word)
    const updateList = keywordList.map((item) => {
      if ((item.word.toLowerCase().indexOf(lowerCasedWord) > -1 || item.word.indexOf(hiragana) > -1 || item.word.indexOf(katakana) > -1)
        && count < KEYWORD_DISPLAY_LIMIT) {
        count += 1
        return { ...item, display: true }
      }
      return { ...item, display: false }
    })
    setKeywordList(updateList)
  }

  const keywordSuggest = (e: React.FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value.trim()
    if (inputValue === "") {
      resetKeywordSuggest()
      return
    }
    setIsKeywordDisplay(true)
    wordHistorySelect(inputValue)
    wordKeywordSelect(inputValue)
  }

  const resetForm = () => {
    setSearchValue({ ...searchValueInit })
    resetHistoryChip()
    resetKeywordChip()
    setDateErrorMessage('')
    setKeywordErrorMessage('')
    setKeywordHintMessage('')
    setSearchErrorMessage('')
  }

  const resetKeyword = () => {
    setSearchValue({ ...searchValue, keywords: [] })
    resetHistoryChip()
    resetKeywordChip()
    setKeywordHintMessage('')
    setClickWord('')
  }

  const [isToTop, setIsToTop] = useState(false)

  const [isDisplaySticky, setIsDisplaySticky] = useState(false)

  const scrollTop = (): number => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)

  const onScroll = (): void => {
    if (scrollTop() <= 800) {
      setIsDisplaySticky(false)
    } else {
      setIsDisplaySticky(true)
    }
    if (scrollTop() <= 1500) {
      setIsToTop(false)
    } else {
      setIsToTop(true)
    }
  }

  const buildUrl = () => {
    const query_array = []
    if (searchValue.start_date) {
      query_array.push(`start_date=${searchValue.start_date}`)
    }
    if (searchValue.end_date) {
      query_array.push(`end_date=${searchValue.end_date}`)
    }
    if (searchValue.team_names.length) {
      query_array.push(`team_ids=${teams?.filter((team) => searchValue.team_names.includes(team.name)).map((team) => team.id) || []}`)
    }
    if (searchValue.member_names.length) {
      query_array.push(`member_ids=${members?.filter((member) => searchValue.member_names.includes(member.name)).map((member) => member.id) || []}`)
    }
    if (searchValue.keywords.length) {
      query_array.push(`keywords=${searchValue.keywords}`)
    }
    if (searchValue.service_names.length) {
      query_array.push(`service_names=${searchValue.service_names}`)
    }
    if (searchValue.category_names.length) {
      query_array.push(`category_ids=${CATEGORY.filter((category) => searchValue.category_names.includes(category.code)).map(
        (category) => category.id,
      ) || []}`)
    }
    if (searchValue.efficiency_level.length) {
      query_array.push(`efficiency=${
        efficiencyScoreList.filter((score) => searchValue.efficiency_level.includes(score.name)).map((score) => score.value) || []
      }`)
    }
    if (searchValue.stress_level.length) {
      query_array.push(`stress=${
        stressScoreList.filter((score) => searchValue.stress_level.includes(score.name)).map((score) => score.value) || []
      }`)
    }
    if (searchValue.focus_level.length) {
      query_array.push(`focus=${
        focusScoreList.filter((score) => searchValue.focus_level.includes(score.name)).map((score) => score.value) || []
      }`)
    }
    if (searchValue.sort_key?.length) {
      query_array.push(`sort_key=${searchValue.sort_key}`)
    }
    if (searchValue.sort_type?.length) {
      query_array.push(`sort_type=${searchValue.sort_type}`)
    }
    const state = location.state as { referrer: string }
    if (state && state?.referrer) {
      history.push(`${setPath(URL.SEARCH.path, language)}?${query_array.join('&')}`, { referrer: state.referrer })
    } else {
      history.push(`${setPath(URL.SEARCH.path, language)}?${query_array.join('&')}`)
    }
  }

  const clickSubmit = () => {
    const errorMessages = validateFormValues(searchValue)
    if (errorMessages.length === 0) {
      setSearchResultLoading(true)
      setIsSearch(true)
      setDateErrorMessage('')
      setKeywordErrorMessage('')
      setKeywordHintMessage('')
      setSearchErrorMessage('')
      const requestParams: SearchRequestParams = {
        start_date: searchValue.start_date,
        end_date: searchValue.end_date,
        team_ids: teams?.filter((team) => searchValue.team_names.includes(team.name)).map((team) => team.id) || [],
        member_ids: members?.filter((member) => searchValue.member_names.includes(member.name)).map((member) => member.id) || [],
        keywords: searchValue.keywords,
        service_names: searchValue.service_names,
        category_ids: CATEGORY.filter((category) => searchValue.category_names.includes(category.code)).map(
          (category) => category.id,
        ) || [],
        efficiency_level:
          efficiencyScoreList.filter((score) => searchValue.efficiency_level.includes(score.name)).map((score) => score.value) || [],
        stress_level:
          stressScoreList.filter((score) => searchValue.stress_level.includes(score.name)).map((score) => score.value) || [],
        focus_level:
          focusScoreList.filter((score) => searchValue.focus_level.includes(score.name)).map((score) => score.value) || [],
        lower_limit_time: searchValue.lower_limit_time,
      }
      if (searchValue.sort_key) {
        requestParams.sort_key = searchValue.sort_key
      }
      if (searchValue.sort_type) {
        requestParams.sort_type = searchValue.sort_type
      }
      dispatch(fetchSearchRequest(
        requestParams,
        () => {
          addWordToHistory(searchValue.keywords)
          setSearchResultLoading(false)
          buildUrl()
          setTimeout(() => {
            scrollToResultTop()
          }, 1000)
        },
      ))
    } else {
      errorMessages.forEach((error: Error) => {
        if (error.name === 'date') {
          setDateErrorMessage(error.message)
        } else if (error.name === 'keyword') {
          setKeywordErrorMessage(error.message)
        } else if (error.name === 'search') {
          setSearchErrorMessage(error.message)
        }
      })
    }
  }

  useEffect(() => {
    const searchParams = location.search
    if (!searchParams || !company_service || !teams || !members) return

    const getParams = getGetPrams(searchParams) as GetParams
    const requestParams = {
      ...searchValue,
      start_date: getParams.start_date,
      end_date: getParams.end_date,
    }
    if (getParams.team_ids) {
      const team_ids = getParams.team_ids.split(',')
      requestParams.team_names = teams?.filter(
        (team) => team_ids.includes(String(team.id)) && team_manager_ids?.includes(team.id)).map((team) => team.name) || []
    }
    if (getParams.member_ids) {
      const member_ids = getParams.member_ids.split(',')
      requestParams.member_names = members?.filter((member) => member_ids.includes(String(member.id))).map((member) => member.name) || []
    }
    if (getParams.search_word) {
      requestParams.keywords.push(decodeURI(getParams.search_word))
    } else if (getParams.keywords) {
      requestParams.keywords = decodeURI(getParams.keywords).split(',')
    }
    if (getParams.service_names) {
      requestParams.service_names = decodeURI(getParams.service_names).split(',')
    }
    if (getParams.category_ids) {
      const category_ids = getParams.category_ids.split(',')
      requestParams.category_names = CATEGORY.filter((category) => category_ids.includes(String(category.id))).map(
        (category) => category.code,
      ) || []
    }
    if (getParams.efficiency) {
      const efficiency = getParams.efficiency.split(',')
      requestParams.efficiency_level = efficiencyScoreList.filter((score) => efficiency.includes(score.value)).map((score) => score.name) || []
    }
    if (getParams.stress) {
      const stress = getParams.stress.split(',')
      requestParams.stress_level = stressScoreList.filter((score) => stress.includes(score.value)).map((score) => score.name) || []
    }
    if (getParams.focus) {
      const focus = getParams.focus.split(',')
      requestParams.focus_level = focusScoreList.filter((score) => focus.includes(score.value)).map((score) => score.name) || []
    }
    if (getParams.sort_key) {
      requestParams.sort_key = getParams.sort_key
    }
    if (getParams.sort_type) {
      requestParams.sort_type = getParams.sort_type
    }

    setSearchValue(requestParams)
    if (
      requestParams.team_names.length
      || requestParams.member_names.length
      || requestParams.keywords.length
      || requestParams.service_names.length
      || requestParams.category_names.length
    ) {
      setIsSubmit(true)
    }
  }, [company_service, teams, members])

  useEffect(() => {
    initHistoryList(searchHistory)
  }, [searchHistory])

  useEffect(() => {
    buildKeywordList(searchKeyword)
    buildWordCloudKeywordList(searchKeyword)
    setTimeout(() => {
      setIsWordCloudLoading(false)
    }, 3000)
  }, [searchKeyword])

  useEffect(() => {
    if (!isSearch) return
    if (!searchResult.search_result) {
      setSearchResultList({ summary: undefined, result_list: undefined, tags: [], search_result: false, search_request: undefined })
    } else if (!searchResult.result_list?.length) {
      setSearchResultList({ summary: undefined, result_list: [], tags: [], search_result: true, search_request: undefined })
    } else {
      setSearchResultList(
        {
          summary: searchResult.summary,
          result_list: searchResult.result_list,
          tags: searchResult.tags,
          search_result: true,
          search_request: searchResult.search_request,
        },
      )
    }
  }, [searchResult])

  useEffect(() => {
    if (!login.accessToken) return
    if (isDateRangeDisplay) return
    if (!isChangeDate) return
    const diff = diffDays(searchValue.start_date, searchValue.end_date)
    if (diff < 0 || diff >= 180) return
    const values = {
      start_date: searchValue.start_date,
      end_date: searchValue.end_date,
    }
    dispatch(fetchKeyword(values))
    setIsWordCloudLoading(true)
    // setIsChangeDate(false)
  }, [isDateRangeDisplay, searchValue.start_date, searchValue.end_date, login.accessToken])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(initialSearchResult())
    dispatch(fetchHistory())
    dispatch(fetchCompanyService())
    dispatch(fetchSearchCondition())
    document.addEventListener("scroll", onScroll)
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [login.accessToken])

  const saveSearchConditions = (title: string) => {
    dispatch(createSearchCondition(
      {
        name: title,
        start_date: searchValue.start_date,
        end_date: searchValue.end_date,
        team_ids: teams?.filter((team) => searchValue.team_names.includes(team.name)).map((team) => team.id) || [],
        member_ids: members?.filter((member) => searchValue.member_names.includes(member.name)).map((member) => member.id) || [],
        keywords: searchValue.keywords,
        service_names: searchValue.service_names,
        category_ids: CATEGORY.filter((category) => searchValue.category_names.includes(category.code)).map(
          (category) => category.id,
        ) || [],
        efficiency_level:
          efficiencyScoreList.filter((score) => searchValue.efficiency_level.includes(score.name)).map((score) => score.value) || [],
        stress_level:
          stressScoreList.filter((score) => searchValue.stress_level.includes(score.name)).map((score) => score.value) || [],
        focus_level:
          focusScoreList.filter((score) => searchValue.focus_level.includes(score.name)).map((score) => score.value) || [],
        lower_limit_time: searchValue.lower_limit_time,
      },
      () => {
        alert.success(t('search.conditions.alert.create.success', { title: title }))
        dispatch(fetchSearchCondition())
      },
      () => {
        alert.error(t('search.conditions.alert.create.error'))
        dispatch(fetchSearchCondition())
      },
    ))
  }

  const updateSearchConditions = (id: number, title: string) => {
    dispatch(updateSearchCondition(
      {
        id: id,
        name: title,
      },
      () => {
        alert.success(t('search.conditions.alert.update.success', { title: title }))
        dispatch(fetchSearchCondition())
      },
      () => {
        alert.error(t('search.conditions.alert.update.error'))
        dispatch(fetchSearchCondition())
      },
    ))
  }

  const deleteSearchConditions = (id: number, title: string) => {
    dispatch(delSearchCondition(
      id,
      () => {
        alert.success(t('search.conditions.alert.delete.success', { title: title }))
        dispatch(fetchSearchCondition())
      },
      () => {
        alert.error(t('search.conditions.alert.update.error'))
        dispatch(fetchSearchCondition())
      },
    ))
  }

  const openMiniMenu = () => {
    setMiniMenuDisplay(true)
  }

  const closeMiniMenu = () => {
    setMiniMenuDisplay(false)
  }

  const selectSearchCondition = (id: number) => {
    const selectCondition = searchCondition.find((value) => value.id === id)
    if (!selectCondition) return
    const team_names = teams?.filter((team) => selectCondition.team_ids.includes(team.id)).map((team) => team.name) || []
    const member_names = members?.filter((member) => selectCondition.member_ids.includes(member.id)).map((member) => member.name) || []
    const category_names = CATEGORY.filter((category) => selectCondition.category_ids.includes(category.id)).map(
      (category) => category.code,
    ) || []
    const efficiency_level = efficiencyScoreList.filter((score) => selectCondition.efficiency_level.includes(score.value)).map(
      (score) => score.name,
    ) || []
    const stress_level = stressScoreList.filter((score) => selectCondition.stress_level.includes(score.value)).map(
      (score) => score.name,
    ) || []
    const focus_level = focusScoreList.filter((score) => selectCondition.focus_level.includes(score.value)).map(
      (score) => score.name,
    ) || []
    setIsSwitchButton(selectCondition.lower_limit_time === LOWER_LIMIT_TIME_DEFAULT)
    setSearchValue({
      ...searchValueInit,
      start_date: selectCondition.start_date,
      end_date: selectCondition.end_date,
      team_names: team_names,
      member_names: member_names,
      keywords: selectCondition.keywords,
      service_names: selectCondition.service_names,
      category_names: category_names,
      efficiency_level: efficiency_level,
      stress_level: stress_level,
      focus_level: focus_level,
      lower_limit_time: selectCondition.lower_limit_time,
    })
    closeMiniMenu()
    setIsSubmit(true)
  }

  // modal
  const openSearchConditionsModal = (type: string, title?: string, id?: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-explicit-any
    switch (type) {
      case "create":
        setSearchConditionsModalState({
          ...searchConditionsModalStateInit,
          display: true,
          type: type,
          callback: (name: string) => saveSearchConditions(name),
        })
        break
      case "edit":
        setSearchConditionsModalState({
          ...searchConditionsModalStateInit,
          display: true,
          type: type,
          title: title,
          id: id,
          callback: (c_title: string, c_id: number) => updateSearchConditions(c_id, c_title),
        })
        break
      default:
        break
    }
  }

  const closeSearchConditionsModal = () => {
    setSearchConditionsModalState(searchConditionsModalStateInit)
  }

  // confirmation modal
  const openConfirmationModal = (id: number, title: string) => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: "delete",
      title: title,
      id: id,
      callback: () => deleteSearchConditions(id, title),
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      title: "",
      callback: () => {
        // none
      },
    })
  }

  const toggleWordcloud = () => {
    setIsDisplayWordCloud(!isDisplayWordCloud)
    setIsWordCloudLoading(true)
    setTimeout(() => {
      setIsWordCloudLoading(false)
    }, 3000)
  }

  useEffect(() => {
    if (!miniMenuDisplay) return
    closeMiniMenu()
  }, [searchConditionsModalState.display, confirmationModalState.display])

  useEffect(() => {
    if (!clickWord) return
    if (searchValue.keywords.includes(clickWord)) return
    addKeyword(clickWord)
  }, [clickWord])

  useEffect(() => {
    if (!isSubmit) return
    clickSubmit()
    setIsSubmit(false)
  }, [isSubmit])

  const wordcloud = useMemo(() => (
    <ReactWordcloud
      words={wordCloudKeywordList.map((value) => ({ text: value.word, value: value.value || 0 })).slice(0, 100)}
      maxWords={100}
      options={{
        rotations: 0,
        deterministic: true,
        fontSizes: [20, 100],
        enableTooltip: false,
        fontWeight: "bold",
        fontFamily: "Noto Sans CJK JP",
      }}
      callbacks={{
        onWordClick: (word) => clickWordCloud(word.text),
      }}
    />
  ), [wordCloudKeywordList])

  const [referrerState, setReferrerState] = useState({
    title: "",
    url: "",
  })

  useEffect(() => {
    const state = location.state as { referrer: string }
    if (!state || !state.referrer || !state.referrer.match("/report")) {
      setReferrerState({
        title: "",
        url: "",
      })
      return
    }
    const referrerPath = state.referrer.split(`${language}/`)[1]
    const referrerArray = referrerPath.split("?")[0].split("/")
    let range = ""
    if (referrerArray[1] === "monthly") {
      range = "月間改善レポート"
    } else {
      range = "週間業務レポート"
    }
    let name = ""
    if (referrerArray[2] === "team") {
      name = `${teams?.find((team) => team.id === Number(referrerArray[3]))?.name || ""}チーム`
    } else {
      name = `${members?.find((member) => member.id === Number(referrerArray[3]))?.name || ""}さん`
    }
    setReferrerState({
      title: `${range} ${name}`,
      url: `${referrerPath}`,
    })
  }, [location])

  return (
    <div className="search_page">
      <Helmet>
        <title>Qasee - 検索 - 全体検索</title>
      </Helmet>
      <section className="breadcrumb">
        検索
        &nbsp;&gt;&nbsp;
        全体検索
        &nbsp;
      </section>
      <section className={`sticky_header ${isDisplaySticky && "active"}`}>
        {
          referrerState.title && (
            <div className="bread_area">
              <FontAwesomeIcon icon={["fas", "history"]} />
              &nbsp;
              <Link
                to={referrerState.url}
              >
                {referrerState.title}
              </Link>
              &nbsp;&gt;&nbsp;検索
            </div>
          )
        }
        <div className="condition_area">
          <div className="mb8">
            対象期間:
            <div className="chip">
              {
                searchValue.start_date === searchValue.end_date ? (
                  <>
                    {searchValue.start_date}
                  </>
                ) : (
                  <>
                    {searchValue.start_date}
                    ~
                    {searchValue.end_date}
                  </>
                )
              }
            </div>
          </div>
          {
            (!!searchValue.team_names?.length || !!searchValue.member_names?.length) && (
              <div className="mb8">
                チーム/メンバー:
                {
                  searchValue.team_names?.map((team_name) => (
                    <div
                      className="no_trans chip"
                      key={`team_${team_name}`}
                    >
                      {team_name}
                    </div>
                  ))
                }
                {
                  searchValue.member_names?.map((member_name) => (
                    <div
                      className="no_trans chip"
                      key={`member_${member_name}`}
                    >
                      {member_name}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            (!!searchValue.category_names?.length || !!searchValue.service_names?.length) && (
              <div className="mb8">
                分類/アプリ:
                {
                  searchValue.category_names?.map((category_name) => (
                    <div
                      className="chip"
                      key={`category_${category_name}`}
                    >
                      {t(`setting.personal.custom_alert.category_name.${category_name}`)}
                    </div>
                  ))
                }
                {
                  searchValue.service_names?.map((service_name) => (
                    <div
                      className="chip"
                      key={`service_${service_name}`}
                    >
                      {service_name}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.keywords?.length && (
              <div className="mb8">
                フリーキーワード:
                {
                  searchValue.keywords.map((keyword) => (
                    <div
                      className="no_trans chip"
                      key={`keyword_${keyword}`}
                    >
                      {keyword}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.efficiency_level?.length && (
              <div className="mb8">
                業務効率適正度:
                {
                  searchValue.efficiency_level.map((efficiency) => (
                    <div
                      className="chip"
                      key={`efficiency_${efficiency}`}
                    >
                      {efficiency}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.stress_level?.length && (
              <div className="mb8">
                業務負荷適正度:
                {
                  searchValue.stress_level.map((stress) => (
                    <div
                      className="chip"
                      key={`stress_${stress}`}
                    >
                      {stress}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.focus_level?.length && (
              <div className="mb8">
                業務集中適正度:
                {
                  searchValue.focus_level.map((focus) => (
                    <div
                      className="chip"
                      key={`focus_${focus}`}
                    >
                      {focus}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.lower_limit_time && (
              <div className="mb8">
                5分以上の作業のみ表示する:
                <div
                  className="chip"
                  key={`lower_limit_time_${searchValue.lower_limit_time}`}
                >
                  オン
                </div>
              </div>
            )
          }
          {
            !!searchValue.sort_key?.length && (
              <div className="mb8">
                並び替え:
                <div
                  className="chip"
                  key={`sort_type_${searchValue.sort_key}`}
                >
                  {t(`search.result.data.sort.${searchValue.sort_type}.${searchValue.sort_key}`)}
                </div>
              </div>
            )
          }
        </div>
      </section>
      <section className="page_header">
        <h1>
          全体検索
        </h1>
        <div className="tools">
          <button
            type="button"
            className="btn search_condition_button"
            onClick={() => openMiniMenu()}
          >
            <ReactSVG
              src="/static/images/icon/icon-view.svg"
              className="icon"
            />
            {t('search.conditions.button.list')}
          </button>
          <MiniMenu
            miniMenuDisplay={miniMenuDisplay}
            searchConditionList={searchCondition}
            closeCallback={closeMiniMenu}
            selectCallback={(id: number) => selectSearchCondition(id)}
            updateCallback={(id: number, title: string) => openSearchConditionsModal("edit", title, id)}
            deleteCallback={(id: number, title: string) => openConfirmationModal(id, title)}
          />
          <button
            type="button"
            className="btn search_condition_button"
            onClick={() => openSearchConditionsModal("create")}
          >
            <ReactSVG
              src="/static/images/icon/icon-save.svg"
              className="icon"
            />
            {t('search.conditions.button.create')}
          </button>
        </div>
      </section>
      <section className="page_description">
        <p>
          データを検索して情報を閲覧することができます。
        </p>
      </section>
      <section className="page_contents search_condition_area">
        <div className="search">
          <form autoComplete="off">
            <div className="flex">
              <div className="block date_area">
                <div className="title">
                  {t('search.conditions.period.title')}
                </div>
                <div style={{ position: "relative" }}>
                  <div className="daterange" role="none" onClick={openDateRangeModal}>
                    {format(searchValue.start_date)}
                    &nbsp;〜&nbsp;
                    {format(searchValue.end_date)}
                  </div>
                </div>
                {
                  dateErrorMessage && (
                    <div className="error_field">
                      {dateErrorMessage}
                    </div>
                  )
                }
                {
                  isDateRangeDisplay && (
                    <>
                      <div className="date_box">
                        <DateRange
                          ranges={[{ startDate: toDate(searchValue.start_date), endDate: toDate(searchValue.end_date), key: 'selection' }]}
                          onChange={changeDateRange}
                          months={2}
                          direction="horizontal"
                          dateDisplayFormat="yyyy/MM/dd"
                          weekStartsOn={0}
                          locale={language === 'en' ? enUS : ja}
                          maxDate={toDate(correctionDate(yesterDay()))}
                          minDate={toDate(company?.created_at)}
                          monthDisplayFormat={language === 'en' ? "MMM yyyy" : "yyyy年MM月"}
                        />
                        <button type="button" className="closebox" onClick={closeDateRangeModal}>
                          <FontAwesomeIcon icon={['far', 'times-circle']} />
                        </button>
                      </div>
                      <div className="search_screen" role="presentation" onClick={closeDateRangeModal} />
                    </>
                  )
                }
              </div>
              <div className="block team_area">
                <div className="title">
                  {t('search.conditions.team_member.title')}
                </div>
                {
                  teams && members ? (
                    <TeamAndMemberKeywordInput
                      placeHolder={t('search.conditions.team_member.placeholder')}
                      team={{
                        modalName: t('search.conditions.team_member.team'),
                        options: teams?.filter((team) => team_manager_ids?.includes(team.id)).map((team) => team.name),
                        keywords: searchValue.team_names,
                        setKeywords: setTeamNames,
                        selectLimit: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT,
                        displayLimit: SERVICE_NAME_DISPLAY_LIMIT,
                      }}
                      member={{
                        modalName: t('search.conditions.team_member.member'),
                        options: members?.map((member) => member.name),
                        keywords: searchValue.member_names,
                        setKeywords: setMemberNames,
                        selectLimit: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT,
                        displayLimit: SERVICE_NAME_DISPLAY_LIMIT,
                      }}
                      resetKeywords={resetTeamNamesAndMemberNames}
                    />
                  ) : (
                    <div className="input_field">
                      <ReactSVG
                        src="/static/images/icon/icon-search.svg"
                        className="icon"
                      />
                      <div className="MuiFormControl-root WAMuiChipInput-root-1">
                        <input
                          aria-invalid="false"
                          placeholder={t('search.conditions.category_app.placeholder')}
                          type="text"
                          className="MuiInputBase-input MuiInput-input WAMuiChipInput-input-3 WAMuiChipInput-standard-6"
                          value=""
                        />
                      </div>
                    </div>
                  )
                }
              </div>
            </div>
            <div className="flex">
              <div className="block category_area">
                <div className="title">
                  {t('search.conditions.category_app.title')}
                </div>
                {company_service ? (
                  <CategoryAndAppKeywordInput
                    placeHolder={t('search.conditions.category_app.placeholder')}
                    category={{
                      modalName: t('search.conditions.category_app.category'),
                      options: CATEGORY.map((val) => val.code),
                      keywords: searchValue.category_names,
                      setKeywords: setCategoryNames,
                      selectLimit: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT,
                      displayLimit: SERVICE_NAME_DISPLAY_LIMIT,
                    }}
                    service={{
                      modalName: t('search.index.services'),
                      options: company_service.application_names,
                      keywords: searchValue.service_names,
                      setKeywords: setServiceNames,
                      selectLimit: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT,
                      displayLimit: SERVICE_NAME_DISPLAY_LIMIT,
                    }}
                    resetKeywords={resetServiceNamesAndCategoryNames}
                  />
                ) : (
                  <div className="input_field">
                    <ReactSVG
                      src="/static/images/icon/icon-search.svg"
                      className="icon"
                    />
                    <div className="MuiFormControl-root WAMuiChipInput-root-1">
                      <input
                        aria-invalid="false"
                        placeholder={t('search.conditions.category_app.placeholder')}
                        type="text"
                        className="MuiInputBase-input MuiInput-input WAMuiChipInput-input-3 WAMuiChipInput-standard-6"
                        value=""
                      />
                    </div>
                  </div>
                )}
              </div>
              {
                login.function_enabled_state?.ai_work_assessment && (
                  <>
                    <div className="block score_area">
                      <div className="title">
                        {t('search.conditions.score.title')}
                      </div>
                      <ScoreKeywordInput
                        placeHolder={t('search.conditions.score.placeholder')}
                        efficiency={{
                          modalName: t('search.conditions.score.efficiency'),
                          options: efficiencyScoreList.map((score) => score.name),
                          keywords: searchValue.efficiency_level,
                          setKeywords: setEfficiencyList,
                          selectLimit: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT,
                          displayLimit: SERVICE_NAME_DISPLAY_LIMIT,
                        }}
                        stress={{
                          modalName: t('search.conditions.score.stress'),
                          options: stressScoreList.map((score) => score.name),
                          keywords: searchValue.stress_level,
                          setKeywords: setStressList,
                          selectLimit: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT,
                          displayLimit: SERVICE_NAME_DISPLAY_LIMIT,
                        }}
                        focus={{
                          modalName: t('search.conditions.score.focus'),
                          options: focusScoreList.map((score) => score.name),
                          keywords: searchValue.focus_level,
                          setKeywords: setFocusList,
                          selectLimit: CATEGORY_AND_SERVICE_NAME_SELECT_LIMIT,
                          displayLimit: SERVICE_NAME_DISPLAY_LIMIT,
                        }}
                        resetKeywords={resetScore}
                      />
                    </div>
                  </>
                )
              }
            </div>
            <div className="flex">
              <div className="block keyword_area">
                <div className="title">
                  {t('search.conditions.keyword.title')}
                </div>
                <div>
                  <div className="input_field">
                    <ReactSVG
                      src="/static/images/icon/icon-search.svg"
                      className="icon"
                    />
                    <ChipInput
                      id="keywords"
                      value={searchValue.keywords}
                      placeholder={t('search.conditions.keyword.placeholder')}
                      onAdd={(chip) => inputKeyword(chip)}
                      onUpdateInput={keywordSuggest}
                      onClick={openKeywordModal}
                      newChipKeyCodes={[13]}
                      newChipKeys={[""]}
                      onBlur={blurKeyword}
                      onFocus={openKeywordModal}
                      chipRenderer={({ value }, key) => (
                        <Chip
                          className="chip no_trans"
                          key={key}
                          label={value}
                          variant="outlined"
                          onDelete={() => deleteKeyword(value, key)}
                          deleteIcon={<div>×</div>}
                        />
                      )}
                    />
                    {
                      searchValue.keywords.length !== 0 && (
                        <button type="button" className="reset" onClick={() => resetKeyword()}>
                          ×
                        </button>
                      )
                    }
                  </div>
                  <div>
                    {
                      isKeywordDisplay && (
                        <>
                          <div className="suggest_box" role="none" onClick={() => setTmpKeyword('')}>
                            <div className="suggest_contents">
                              <div className="title">
                                <FontAwesomeIcon icon={['fas', 'clock']} />
                                &nbsp;
                                {t('search.index.recent_searches')}
                              </div>
                              {
                                historyList.filter((value) => value.display).length ? (
                                  <>
                                    <div className="chips no_trans">
                                      {
                                        historyList.filter((value) => value.display).map((value) => (
                                          value.check ? (
                                            <button type="button" key={value.word} className="checked">
                                              {value.word}
                                            </button>
                                          ) : (
                                            <button type="button" key={value.word} onClick={() => addKeyword(value.word)}>
                                              {value.word}
                                            </button>
                                          )
                                        ))
                                      }
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="no_data">
                                      {t('search.index.no_data')}
                                    </div>
                                  </>
                                )
                              }
                              <div className="title">
                                <FontAwesomeIcon icon={['fas', 'search']} />
                                &nbsp;
                                {t('search.index.used_words', {
                                  date: `${format(searchValue.start_date)} 〜 ${format(searchValue.end_date)}` })}
                              </div>
                              {
                                keywordList.filter((value) => value.display).length ? (
                                  <>
                                    <div className="chips no_trans">
                                      {
                                        keywordList.filter((value) => value.display).map((value) => (
                                          value.check ? (
                                            <button type="button" key={value.word} className="checked">
                                              {value.word}
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              key={value.word}
                                              className={`rank_${value.rank}`}
                                              onClick={() => addKeyword(value.word)}
                                            >
                                              {value.word}
                                            </button>
                                          )
                                        ))
                                      }
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="no_data">
                                      {t('search.index.no_data')}
                                    </div>
                                  </>
                                )
                              }
                            </div>
                            <button type="button" className="closebox" onClick={closeKeywordModal}>
                              <FontAwesomeIcon icon={['far', 'times-circle']} />
                            </button>
                          </div>
                          <div className="search_screen" role="presentation" onClick={closeKeywordModal} />
                        </>
                      )
                    }
                  </div>
                </div>
                {
                  keywordErrorMessage && (
                    <div className="error_field">
                      {keywordErrorMessage}
                    </div>
                  )
                }
                {
                  keywordHintMessage && (
                    <div className="hint_field">
                      {keywordHintMessage}
                    </div>
                  )
                }
              </div>
              <div className="block working_time_area">
                <div className="title">
                  {t('search.conditions.working_time_area.title')}
                </div>
                <div className="working_time">
                  <input
                    id="btn-mode"
                    type="checkbox"
                    checked={isSwitchButton}
                    onChange={switchLowerLimitTime}
                  />
                  <label htmlFor="btn-mode">
                    <span className={isSwitchButton ? '' : 'working_time_off'}>
                      {isSwitchButton ? 'オン' : 'オフ'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="block button_area">
              {
                !isLoading && (
                  <>
                    <button className="btn reset" type="button" onClick={resetForm} disabled={isLoading}>
                      {t('search.index.form.reset')}
                    </button>
                    <button className="btn submit" type="button" onClick={clickSubmit} disabled={isLoading}>
                      {t('search.index.form.search')}
                    </button>
                  </>
                )
              }
            </div>
            <div className="block center wordcloud_toggle_area">
              <div
                onClick={() => toggleWordcloud()}
                role="none"
                className="wordcloud_toggle"
              >
                {t('search.index.wordcloud')}
                <div className="search_condition_open_button">
                  <div
                    className={`search_condition_open_check ${isDisplayWordCloud && "checked"}`}
                  />
                  <div />
                </div>
              </div>
            </div>
            {
              isDisplayWordCloud ? (
                <div className="block center">
                  <div className="wordcloud_search wordcloud_wrap">
                    <div className={`loading_area ${isWordCloudLoading || "none"}`}>
                      <div className="loading_wrapper">
                        <div className="spinner type1" />
                        <span>
                          {t('search.index.image_loading_message')}
                        </span>
                      </div>
                    </div>
                    {
                      wordCloudKeywordList.length ? (
                        <>
                          <div className="wordcloud_search_image">{wordcloud}</div>
                        </>
                      ) : (
                        <>
                          <img src="/static/images/wordcloud-sample.png" alt="" className="wordcloud_search_image no_data" />
                          <div className="no_image">
                            <div className="title">
                              {t('search.index.no_image.title', {
                                date: `${format(searchValue.start_date)} 〜 ${format(searchValue.end_date)}` })}
                            </div>
                            <div className="message">
                              {t('search.index.no_image.message')}
                            </div>
                          </div>
                        </>
                      )
                    }
                  </div>
                </div>
              ) : (
                <></>
              )
            }
            {
              searchErrorMessage && (
                <div className="block center">
                  <div className="error_field">
                    {searchErrorMessage}
                  </div>
                </div>
              )
            }
          </form>
        </div>
      </section>
      <div ref={resultTopRef} />
      {
        isSearch && (
          <SearchResult
            searchValue={searchValue}
            searchResult={searchResultList}
            isLoading={searchResultLoading}
            displayGraph={login.function_enabled_state?.ai_work_assessment || false}
          />
        )
      }
      <div className="top_back">
        <button type="button" onClick={scrollToResultTop} className={isToTop ? 'active' : ''}>
          <FontAwesomeIcon icon={['fas', 'chevron-up']} size="2x" />
        </button>
      </div>

      {/* 検索条件モーダル */}
      <SearchConditionsModal
        modalState={searchConditionsModalState}
        closeCallback={closeSearchConditionsModal}
        titleList={searchCondition.map((value) => value.name)}
      />

      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </div>
  )
}

export default Index
